import React, { useState, useEffect } from "react";
import { type ButtonItem } from "../../../../components/SbuxButtonGroup";
import type { MediaInfo } from "./types";
import SbuxDialog2 from "../../../../components/SbuxDialog2";
import SbuxStackSnackbar from "../../../../components/SbuxStackSnackbar";
import SbuxTranslate from "../../../../components/SbuxTranslate";
import MediaMgtAdd from "../table/mediaMgt/MediaMgtAdd";
import MediaMgtEdit from "../table/mediaMgt/MediaMgtEdit";
import { Box, Button, DialogContent, DialogActions } from "@mui/material";
import { useAppDispatch } from "../../../../store/types";
import { createOrUpdateMediaMgt } from "../../../../services/mediaMgt";
import {
  MM_FILE_UPLOAD_OPTIONS_LIST,
  MM_DEFAULT_LANGUAGES,
  MEDIAMGT_FILE_TYPE_ERROR,
  MEDIAMGT_INVALID_FILE_ERROR,
  SNACK_BARS_SEVERITY_ERROR,
} from "../../../../constants";
import { getResetTextToSpeech } from "../../../../services/textToSpeech";

interface Language {
  value: string;
  name: string;
  [key: string]: unknown;
}

interface MediaMgtRow {
  languageCode?: string;
  language?: string;
  promptName?: string;
  fileName?: string;
  description?: string;
  fileContent?: string;
  mediaId?: string;
  isTextToSpeech?: boolean;
  errorType?: string;
  [key: string]: unknown;
}

interface Props {
  title: React.ReactNode;
  open: boolean;
  toggleDialogOpen: () => void;
  row?: Record<string, unknown> | null;
  languages: Language[];
  isAddNew: boolean;
  mediaContent?: string;
}

const MediaMgtDialog = ({
  title,
  open,
  toggleDialogOpen,
  row,
  languages,
  isAddNew,
  mediaContent,
}: Props) => {
  const dispatch = useAppDispatch();

  const defaultRow: MediaMgtRow = {
    languageCode: "en",
    language: "English",
    promptName: "",
    fileName: "",
    description: "",
    fileContent: "",
  };
  const data = row?.languageCode ? (row as MediaMgtRow) : defaultRow;
  const [mediaMgtRow, setMediaMgtRow] = useState<MediaMgtRow>(data);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [buttonList, setButtonList] = useState<ButtonItem[]>(
    MM_FILE_UPLOAD_OPTIONS_LIST as ButtonItem[],
  );
  const [mediaInfo, setMediaInfo] = useState<MediaInfo | null>(null);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(true);
  const [formValidationError, setFormValidationError] = useState(false);
  const [textLengthValidationError, setTextLengthValidationError] =
    useState(false);

  useEffect(() => {
    setIsDialogOpen(open);
  }, [open]);

  useEffect(() => {
    if (mediaInfo?.errorType) {
      updateMediaMgtRow(true, null, undefined);
    } else if (mediaInfo && mediaInfo.fileContent) {
      updateMediaMgtRow(false, mediaInfo.fileContent, mediaInfo.isTextToSpeech);
    } else {
      updateMediaMgtRow(false, null, undefined);
    }
  }, [mediaInfo]);

  const updateMediaMgtRow = (
    errorType: boolean,
    fileContent: string | null,
    isTextToSpeech: boolean | undefined,
  ) => {
    if (errorType && mediaInfo) {
      setMediaMgtRow({
        ...mediaMgtRow,
        errorType: mediaInfo.errorType,
      });
    } else if (fileContent && mediaInfo) {
      setMediaMgtRow({
        ...mediaMgtRow,
        fileName: mediaMgtRow.fileName
          ? mediaMgtRow.fileName
          : mediaInfo.fileName,
        fileContent:
          mediaInfo.fileContent && mediaInfo.fileContent.split(`,`)[1],
        isTextToSpeech,
      });
      updateButtonList();
    } else {
      setMediaMgtRow({
        ...mediaMgtRow,
      });
      setButtonList(MM_FILE_UPLOAD_OPTIONS_LIST as ButtonItem[]);
    }

    !errorType && mediaMgtRow.promptName
      ? setButtonDisabled(false)
      : setButtonDisabled(true);
    if (!fileContent) {
      setButtonDisabled(true);
    }
    if (formValidationError) {
      setButtonDisabled(true);
    }
    setOpenSnackBar(errorType ? true : false);
  };

  const updateButtonList = () => {
    if (!mediaInfo) return;
    const result = buttonList.map((cur) => {
      if (
        (cur.value === 1 &&
          (mediaInfo.isRecorded || mediaInfo.isTextToSpeech)) ||
        (cur.value === 2 &&
          (mediaInfo.isUploaded || mediaInfo.isTextToSpeech)) ||
        (cur.value === 3 && (mediaInfo.isUploaded || mediaInfo.isRecorded))
      ) {
        return {
          ...cur,
          disabled: true,
          toolTipMsg: (
            <SbuxTranslate>{`To re-enable this tab, delete the ${
              mediaInfo.isRecorded
                ? "recorded"
                : mediaInfo.isTextToSpeech
                  ? "text to speech"
                  : "uploaded"
            } prompt.`}</SbuxTranslate>
          ),
        };
      } else {
        return cur;
      }
    });
    setButtonList(result);
  };

  const handleClose = () => {
    setOpenSnackBar(false);
  };

  const handleSave = async () => {
    const result = {
      languageCode: mediaMgtRow.languageCode,
      language: mediaMgtRow.language,
      promptName: mediaMgtRow.promptName,
      fileName: mediaMgtRow.promptName,
      description: mediaMgtRow.description,
      fileContent: mediaMgtRow.mediaId
        ? mediaMgtRow.fileContent ||
          (mediaContent ? mediaContent.split(",")[1] : undefined)
        : mediaMgtRow.fileContent,
      fileType: mediaMgtRow.isTextToSpeech ? "audio/mp3" : "audio/wav",
      mediaId: mediaMgtRow.mediaId,
    };
    dispatch(createOrUpdateMediaMgt(result as Record<string, unknown>));
    dispatch(getResetTextToSpeech());
    toggleDialogOpen();
  };

  const handleMediaMgtRow = (row: Partial<MediaMgtRow>, isError = false) => {
    const updatedRow: MediaMgtRow = {
      ...mediaMgtRow,
      ...row,
    };
    setFormValidationError(isError);
    setMediaMgtRow(updatedRow);
    (updatedRow.promptName && updatedRow.fileName) ||
    (updatedRow.promptName && updatedRow.fileContent)
      ? setButtonDisabled(false)
      : setButtonDisabled(true);
    if (isError) {
      setButtonDisabled(true);
    }
    if (isAddNew) {
      if (!mediaInfo || !mediaInfo.fileContent) {
        setButtonDisabled(true);
      }
    }
  };

  const handleDisableSaveBtn = (value: boolean) => {
    setTextLengthValidationError(value);
  };
  const handleMediaInfo = (value: MediaInfo | null) => {
    setMediaInfo(value);
  };

  const handleLanguages = (): Language[] => {
    let langugageList: Language[] = [];
    if (row?.languageCode) {
      langugageList.push({
        name: row.language as string,
        value: row.languageCode as string,
      });
    } else {
      const languageCodes = new Set(
        (MM_DEFAULT_LANGUAGES as Language[]).map((d) => d.value),
      );
      langugageList = [
        ...(MM_DEFAULT_LANGUAGES as Language[]),
        ...languages.filter((f) => !languageCodes.has(f.value)),
      ];
    }
    return langugageList;
  };

  const dialogConfig = {
    title,
  };

  return (
    <>
      <SbuxDialog2
        open={isDialogOpen}
        config={dialogConfig}
        handleOnClose={toggleDialogOpen}
      >
        <DialogContent dividers>
          {isAddNew ? (
            <MediaMgtAdd
              languages={handleLanguages()}
              row={mediaMgtRow}
              handleMediaMgtRow={handleMediaMgtRow}
              handleMediaInfo={handleMediaInfo}
              buttonList={buttonList}
              handleDisableSaveBtn={handleDisableSaveBtn}
            />
          ) : (
            <MediaMgtEdit
              row={mediaMgtRow}
              mediaContent={mediaContent}
              handleMediaMgtRow={handleMediaMgtRow}
              handleMediaInfo={handleMediaInfo}
              buttonList={buttonList}
              handleDisableSaveBtn={handleDisableSaveBtn}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Box className="actionContainer">
            <Button
              variant="contained"
              size="medium"
              onClick={handleSave}
              disabled={buttonDisabled || textLengthValidationError}
            >
              <SbuxTranslate>{"Save"}</SbuxTranslate>
            </Button>
            <Button variant="outlined" size="medium" onClick={toggleDialogOpen}>
              <SbuxTranslate>{"Cancel"}</SbuxTranslate>
            </Button>
          </Box>
        </DialogActions>
      </SbuxDialog2>

      {openSnackBar && mediaMgtRow?.errorType && (
        <SbuxStackSnackbar
          width={380}
          open={openSnackBar}
          anchorOriginHorizontal={`center`}
          message={
            mediaMgtRow?.errorType === "invalid"
              ? MEDIAMGT_INVALID_FILE_ERROR
              : MEDIAMGT_FILE_TYPE_ERROR
          }
          severity={mediaMgtRow?.errorType ? SNACK_BARS_SEVERITY_ERROR : ""}
          handleClose={handleClose}
        />
      )}
    </>
  );
};

export default MediaMgtDialog;
